<template>
    <div>
        <GoogleMap
            ref="mapRef"
            :api-key="googleApiKey"
            :api-promise="apiPromise"
            mapId="RouteMap"
            :center="{'lat':mapCenter.lat, 'lng':mapCenter.lng}"
            :zoom="zoom"
            :libraries="['places', 'geometry']"
            style="height: 100%; width: 100%;"
        >
            <!-- render markers and lines -->
            <template v-for="marker in mapMarkers">
                <AdvancedMarker :options="marker.core_options" @click="$emit('onMarkerClicked', marker)">
                    <template #content>
                        <MapCircle size="small" :isHighlightedStop="marker.extra_options.isHighlightedStop" />
                        <TripLabel v-if="!hideFromAndTo && marker.extra_options.id" :labelType="marker.extra_options.id" />
                    </template>
                </AdvancedMarker>
            </template>

            <template v-for="line in mapLines">
                <Polyline :options="line"/>
            </template>
        </GoogleMap>
    </div>
</template>

<script>
import { usePage } from '@inertiajs/vue3';
import { findIndex } from "lodash";
import { defineComponent, ref, watch } from "vue";
import {AdvancedMarker, GoogleMap, Polyline} from "vue3-google-map";
import {loadGoogleJavascript} from "../utils/google";
import MapCircle from "./Markers/MapCircle.vue";
import TripLabel from "./Markers/TripLabel.vue";

function buildMarkers(stops, highlightedStops) {
    return stops.map((stop) => {
        const index = findIndex(highlightedStops, (hs) => hs.code === stop.code);
        let isHighlightedStop = index > -1;

        return {
            core_options: {
                title: stop.name ?? '',
                position: {
                    lat: parseFloat(stop.location.lat),
                    lng: parseFloat(stop.location.lng),
                },
            },
            extra_options: {
                stop,
                isHighlightedStop,
            }
        }
    });
}

export default defineComponent({
    name: "RouteMap",
    components: {
        TripLabel,
        MapCircle,
        GoogleMap,
        Polyline,
        AdvancedMarker,
    },

    props: {
        zoom: {
            type: Number,
            default: 11,
        },
        center: {
            type: Object,
            default: () => ({lat: 33.451284, lng: -112.07454}),
        },
        centerOnBounds: {
            type: Boolean,
            default: true,
        },
        shape: {
            type: Object,
            required: true,
        },
        route: {
            type: [Object, null],
            required: false,
            default: null,
        },
        highlightedLeg: {
            type: Number,
            required: false,
            default: -1,
        },
        highlightedStops: {
            type: Array,
            required: false,
            default: () => [],
        },
        hideFromAndTo: {
            type: Boolean,
            default: false,
        },
        draggableFromAndTo: {
            type: Boolean,
            default: false,
        },
        clickableFromAndTo: {
            type: Boolean,
            default: false,
        },
    },

    setup(props, {emit}) {
        const mapRef = ref(null);
        const isMapVisibleOnSmallScreens = ref(false);
        const googleApiKey = usePage().props.global.google.maps_api_key;
        const apiPromise = ref(null);

        apiPromise.value = loadGoogleJavascript(googleApiKey);

        // Change the map visibility for small screens
        function setMapVisibilityOnSmallScreens(isVisible) {
            isMapVisibleOnSmallScreens.value = isVisible;
        }

        // Build trip markers and lines.
        const mapLines = ref([]);
        const mapMarkers = ref([]);

        function buildLines(lines, { color, isLightColor = false }) {

            return (lines || []).map((line) => {
                const path = line.coordinates.split(' ').map((coord) => {
                    const [lng, lat] = coord.split(',');

                    return {
                        lat: parseFloat(lat),
                        lng: parseFloat(lng),
                    }
                });

                return {
                    path,
                    geodesic: true,
                    strokeColor: `#${color}`,
                    strokeWeight: isLightColor ? 3 : 2,
                    strokeOpacity: isLightColor ? 1 : 0.5,
                    icons: [],
                };
            });
        }

        // Generate the markers.
        function updateMarkersAndLines() {

            const originMarkerFn = () => {
                const stop = props.shape.stops[0];

                const index = findIndex(props.highlightedStops, (hs) => hs.code === stop.code);
                let isHighlightedStop = index > -1;

                if (!stop?.location) {
                    return [];
                }

                const title = (props.hideFromAndTo)
                    ? stop.name
                    : `From: ${stop.name}`;

                return [{
                    core_options: {
                       title,
                        gmpClickable: props.clickableFromAndTo,
                        gmpDraggable: props.draggableFromAndTo,
                        position: {
                            lat: parseFloat(stop.location.lat),
                            lng: parseFloat(stop.location.lng),
                        },
                        zIndex: 10,
                    },
                    extra_options: {
                        id: 'origin',
                        stop,
                        isHighlightedStop,
                    }
                }];
            };

            const destMarkerFn = () => {
                const stop = props.shape.stops[props.shape.stops.length - 1];

                const index = findIndex(props.highlightedStops, (hs) => hs.code === stop.code);
                let isHighlightedStop = index > -1;

                if (!stop?.location) {
                    return [];
                }

                const title = (props.hideFromAndTo)
                    ? stop.name
                    : `To: ${stop.name}`;

                return [{
                    core_options: {
                        title,
                        gmpClickable: props.clickableFromAndTo,
                        gmpDraggable: props.draggableFromAndTo,
                        position: {
                            lat: parseFloat(stop.location.lat),
                            lng: parseFloat(stop.location.lng),
                        },
                        zIndex: 10,
                    },
                    extra_options: {
                        id: 'destination',
                        stop,
                        isHighlightedStop,
                    }
                }];
            };

            // Exclude the origin and destination stops as they are rendered by originMarkerFn and destMarkerFn functions
            const stops = (props.shape.stops || []).slice(1, props.shape.stops.length - 1);

            mapMarkers.value = [
                ...originMarkerFn(),
                ...destMarkerFn(),
                ...buildMarkers(stops, props.highlightedStops),
            ];

            mapLines.value = buildLines(props.shape.lines, props.route);
        }

        const defaultMapCenter = {lat: 33.451284, lng: -112.07454};
        const mapCenter = ref(props.center);

        function setMapCenter({lat, lng}) {
            mapCenter.value = {lat, lng};
        }

        function centerMapOnBounds() {
            setMapCenter(props?.shape?.bounds?.center ? props.shape.bounds.center : defaultMapCenter);
        }

        function updateMap() {
            updateMarkersAndLines();

            if (props.centerOnBounds) {
                centerMapOnBounds();
            }
        }

        // Fit the map into the selected route bounds.
        watch(() => [props.route, props.shape], () => updateMap());
        watch(() => props.highlightedStops, () => updateMarkersAndLines());

        // Emit an event when the Google maps library is ready.
        watch(() => mapRef.value?.ready, () => {
            if (mapRef.value.ready) {
                emit('ready', mapRef);
                updateMap();
            }
        });

        function locationMarkerDragged(locationType, event) {
            const newLocation = {
                lat: event.latLng.lat(),
                lng: event.latLng.lng(),
            };

            emit('location-changed', {
                from: locationType === 'origin' ? newLocation : props.locationFrom,
                to: locationType === 'destination' ? newLocation : props.locationTo,
            });
        }

        return {
            mapRef,
            mapLines,
            mapMarkers,
            mapCenter,
            googleApiKey,
            setMapCenter,
            locationMarkerDragged,
            apiPromise,
        }
    }
})
</script>

<style>
</style>
